import { c as _c } from "react/compiler-runtime";
import { LinkButton, LoadingSpinner, PageShell } from '@onefootprint/ui';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { PasskeyProcessingResult, type PasskeyProcessingResultProps } from '@/idv2/features/identify';
import useD2pStatusPoll, { isTerminalStatus } from '@/idv2/hooks/use-d2p-status-poll';
import { useFootprint } from '@/idv2/hooks/use-footprint';
import { getLogger } from '@/idv2/utils/logger';
const {
  logInfo,
  logWarn
} = getLogger({
  name: 'idv2',
  location: 'passkey-processing'
});
const processingSearchSchema = z.object({
  authToken: z.string(),
  validationToken: z.string(),
  scopedAuthToken: z.string()
});
export const Route = createFileRoute('/identify/passkey-processing')({
  validateSearch: processingSearchSchema,
  component: PasskeyProcessingRoute
});
function PasskeyProcessingRoute() {
  const $ = _c(34);
  const {
    validationToken,
    scopedAuthToken
  } = Route.useSearch();
  const footprint = useFootprint();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "identify.passkeyProcessing"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("idv2", t0);
  const hasProceedRef = useRef(false);
  const [outcome, setOutcome] = useState();
  let t1;
  if ($[1] !== footprint || $[2] !== validationToken) {
    t1 = () => {
      if (hasProceedRef.current) {
        return;
      }
      hasProceedRef.current = true;
      logInfo("Passkey processing complete, proceeding");
      footprint.navigation.identifyComplete(validationToken);
    };
    $[1] = footprint;
    $[2] = validationToken;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const proceedToDone = t1;
  let t2;
  if ($[4] !== proceedToDone) {
    t2 = status => {
      if (status === "completed") {
        logInfo("Passkey registration completed via D2P");
        proceedToDone();
      } else {
        if (status === "canceled") {
          logInfo("Passkey registration cancelled via D2P");
          setOutcome("canceled");
        } else {
          if (status === "failed") {
            logWarn("Passkey D2P session failed");
            setOutcome("failed");
          }
        }
      }
    };
    $[4] = proceedToDone;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== scopedAuthToken || $[7] !== t2) {
    t3 = {
      authToken: scopedAuthToken,
      onStatusChange: t2
    };
    $[6] = scopedAuthToken;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const d2pPoll = useD2pStatusPoll(t3);
  let t4;
  if ($[9] !== d2pPoll) {
    t4 = async () => {
      logInfo("Cancelling passkey D2P session");
      ;
      try {
        await d2pPoll.cancel();
      } catch (t5) {
        const error = t5;
        logWarn("Failed to cancel D2P session", error);
      }
      setOutcome("canceled");
    };
    $[9] = d2pPoll;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const handleCancel = t4;
  let t5;
  if ($[11] !== d2pPoll || $[12] !== handleCancel || $[13] !== outcome) {
    t5 = () => {
      if (outcome) {
        return;
      }
      const passkeyWindow = (window as Window & {
        __fp_passkey_window?: Window;
      }).__fp_passkey_window;
      if (!passkeyWindow || passkeyWindow.closed) {
        return;
      }
      const checkInterval = window.setInterval(() => {
        if (passkeyWindow.closed) {
          window.clearInterval(checkInterval);
          if (isTerminalStatus(d2pPoll.status)) {
            return;
          }
          handleCancel();
        }
      }, 1000);
      return () => window.clearInterval(checkInterval);
    };
    $[11] = d2pPoll;
    $[12] = handleCancel;
    $[13] = outcome;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== outcome) {
    t6 = [outcome];
    $[15] = outcome;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  useEffect(t5, t6);
  if (outcome) {
    let t7;
    if ($[17] !== outcome || $[18] !== proceedToDone) {
      t7 = <PasskeyProcessingResult outcome={outcome} onContinue={proceedToDone} />;
      $[17] = outcome;
      $[18] = proceedToDone;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    return t7;
  }
  let t7;
  if ($[20] !== t) {
    t7 = t(_temp);
    $[20] = t;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== t) {
    t8 = t(_temp2);
    $[22] = t;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <LoadingSpinner size="xlarge" />;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t) {
    t10 = t(_temp3);
    $[25] = t;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== handleCancel || $[28] !== t10) {
    t11 = <div className="flex flex-col items-center pt-6 gap-6">{t9}<LinkButton onClick={handleCancel}>{t10}</LinkButton></div>;
    $[27] = handleCancel;
    $[28] = t10;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== t11 || $[31] !== t7 || $[32] !== t8) {
    t12 = <PageShell title={t7} subtitle={t8}>{t11}</PageShell>;
    $[30] = t11;
    $[31] = t7;
    $[32] = t8;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  return t12;
}
function _temp3($_1) {
  return $_1.cancel;
}
function _temp2($_0) {
  return $_0.subtitle;
}
function _temp($) {
  return $.title;
}